import { useEffect, useState } from "react";
import { supabase } from "./supabase";

// Hand-typed mirror of the migration in
// supabase/migrations/20260527120000_tiktok_ads_schema.sql. Will be replaced
// with generated `Database["public"]["Tables"]` types after the migration
// lands on main and `supabase gen types` is re-run.

export type TikTokAdvertiser = {
  id: string;
  name: string | null;
  status: string | null;
  currency: string | null;
  timezone: string | null;
  country: string | null;
  industry: string | null;
  brand: string | null;
  raw: unknown;
  synced_at: string;
};

export type TikTokCampaign = {
  id: string;
  advertiser_id: string;
  name: string;
  status: string;
  operation_status: string | null;
  objective_type: string | null;
  budget_mode: string | null;
  budget: number | null;
  is_smart_performance_campaign: boolean | null;
  start_time: string | null;
  end_time: string | null;
  raw: unknown;
  synced_at: string;
};

export type TikTokCampaignMetricsDaily = {
  campaign_id: string;
  advertiser_id: string;
  date: string;
  impressions: number;
  reach: number;
  clicks: number;
  spend: number;
  cpc: number | null;
  cpm: number | null;
  ctr: number | null;
  conversions: number;
  conversion_value: number;
  raw: unknown;
  synced_at: string;
};

export type TikTokSyncRun = {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: "running" | "ok" | "error";
  campaigns_upserted: number | null;
  metrics_upserted: number | null;
  error: string | null;
  raw: unknown;
};

export type TikTokAdsData = {
  advertiser: TikTokAdvertiser | null;
  campaigns: TikTokCampaign[];
  metrics: TikTokCampaignMetricsDaily[];
  syncRuns: TikTokSyncRun[];
  loading: boolean;
  error: string | null;
};

const LOOKBACK_DAYS = 30;

export function useTikTokAdsData(): TikTokAdsData {
  const [advertiser, setAdvertiser] = useState<TikTokAdvertiser | null>(null);
  const [campaigns, setCampaigns] = useState<TikTokCampaign[]>([]);
  const [metrics, setMetrics] = useState<TikTokCampaignMetricsDaily[]>([]);
  const [syncRuns, setSyncRuns] = useState<TikTokSyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const client = supabase();
    let cancelled = false;

    async function fetchAll() {
      try {
        const since = new Date();
        since.setDate(since.getDate() - LOOKBACK_DAYS);
        const sinceIso = since.toISOString().slice(0, 10);

        const [a, c, m, r] = await Promise.all([
          client.from("tiktok_ads_advertisers").select("*").limit(1).maybeSingle(),
          client.from("tiktok_ads_campaigns").select("*").order("name"),
          client.from("tiktok_ads_campaign_metrics_daily").select("*").gte("date", sinceIso),
          client.from("tiktok_ads_sync_runs").select("*").order("started_at", { ascending: false }).limit(20),
        ]);

        if (cancelled) return;
        if (a.error && a.error.code !== "PGRST116") throw a.error;
        if (c.error) throw c.error;
        if (m.error) throw m.error;
        if (r.error) throw r.error;

        setAdvertiser((a.data as TikTokAdvertiser) ?? null);
        setCampaigns((c.data as TikTokCampaign[]) ?? []);
        setMetrics((m.data as TikTokCampaignMetricsDaily[]) ?? []);
        setSyncRuns((r.data as TikTokSyncRun[]) ?? []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();

    const channel = client
      .channel("tiktok-ads-data")
      .on("postgres_changes", { event: "*", schema: "public", table: "tiktok_ads_advertisers" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "tiktok_ads_campaigns" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "tiktok_ads_campaign_metrics_daily" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "tiktok_ads_sync_runs" }, fetchAll)
      .subscribe();

    return () => {
      cancelled = true;
      client.removeChannel(channel);
    };
  }, []);

  return { advertiser, campaigns, metrics, syncRuns, loading, error };
}

export type TikTokKpis = {
  impressions: number;
  reach: number;
  clicks: number;
  spend: number;
  conversions: number;
  conversionValue: number;
  ctr: number;
  avgCpc: number;
};

export function aggregateTikTokKpis(rows: TikTokCampaignMetricsDaily[]): TikTokKpis {
  let impressions = 0;
  let reach = 0;
  let clicks = 0;
  let spend = 0;
  let conversions = 0;
  let conversionValue = 0;
  for (const r of rows) {
    impressions += r.impressions;
    reach = Math.max(reach, r.reach);
    clicks += r.clicks;
    spend += Number(r.spend);
    conversions += Number(r.conversions);
    conversionValue += Number(r.conversion_value);
  }
  return {
    impressions,
    reach,
    clicks,
    spend,
    conversions,
    conversionValue,
    ctr: impressions > 0 ? clicks / impressions : 0,
    avgCpc: clicks > 0 ? spend / clicks : 0,
  };
}

export function aggregateTikTokByCampaign(
  campaigns: TikTokCampaign[],
  rows: TikTokCampaignMetricsDaily[],
): Array<TikTokCampaign & { kpis: TikTokKpis }> {
  const byCampaign = new Map<string, TikTokCampaignMetricsDaily[]>();
  for (const r of rows) {
    const list = byCampaign.get(r.campaign_id) ?? [];
    list.push(r);
    byCampaign.set(r.campaign_id, list);
  }
  return campaigns.map((c) => ({
    ...c,
    kpis: aggregateTikTokKpis(byCampaign.get(c.id) ?? []),
  }));
}
